import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Send, User, AlertCircle, Clock } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

// Real staff-to-staff direct chat (Phase A). Talks ONLY to /api/v1/staff-chat/* — never the
// customer ticket/conversation flow. Tenant + staff scoping is enforced server-side.
const StaffChatModal = ({ open, onClose, peer, businessId, selfUid, makeAuthenticatedRequest }) => {
  const { t } = useLanguage();
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  // Optimistic outbound messages we have NOT yet confirmed persisted. Each: {tempId, body, status}.
  // status: 'sending' -> 'failed' (never 'sent'; on success the pending item is removed and the
  // server-confirmed message is appended to `messages`). We never show a fake "sent" locally.
  const [pending, setPending] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const endRef = useRef(null);

  const scrollToEnd = () => endRef.current?.scrollIntoView({ behavior: 'smooth' });

  // Open/create the 1:1 conversation when the modal opens for a peer.
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      if (!open || !peer?.id || !businessId) return;
      setLoading(true); setError(null); setMessages([]); setPending([]); setConversationId(null);
      try {
        const resp = await makeAuthenticatedRequest('/api/v1/staff-chat/conversations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ business_id: businessId, peer_uid: peer.id }),
        });
        if (!resp.ok) throw new Error(`Could not open chat (${resp.status})`);
        const data = await resp.json();
        if (cancelled) return;
        setConversationId(data.conversation?.conversation_id || null);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not open chat');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    init();
    return () => { cancelled = true; };
  }, [open, peer?.id, businessId, makeAuthenticatedRequest]);

  const loadMessages = useCallback(async () => {
    if (!conversationId) return;
    try {
      const resp = await makeAuthenticatedRequest(
        `/api/v1/staff-chat/conversations/${conversationId}/messages?limit=50`
      );
      if (!resp.ok) return;
      const data = await resp.json();
      setMessages(data.messages || []);
      // best-effort mark read
      makeAuthenticatedRequest(`/api/v1/staff-chat/conversations/${conversationId}/read`, { method: 'POST' });
    } catch (_) { /* polling — ignore transient errors */ }
  }, [conversationId, makeAuthenticatedRequest]);

  // Poll messages while open.
  useEffect(() => {
    if (!open || !conversationId) return;
    loadMessages();
    const t = setInterval(loadMessages, 4000);
    return () => clearInterval(t);
  }, [open, conversationId, loadMessages]);

  useEffect(() => { scrollToEnd(); }, [messages, pending]);

  // Persist one message body. Returns true on confirmed backend success, false otherwise.
  const postBody = useCallback(async (body) => {
    if (!conversationId) return false;
    const resp = await makeAuthenticatedRequest(
      `/api/v1/staff-chat/conversations/${conversationId}/messages`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ body }) }
    );
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const data = await resp.json();
    if (data.message) setMessages((prev) => [...prev, data.message]);
    return true;
  }, [conversationId, makeAuthenticatedRequest]);

  const send = async () => {
    const body = draft.trim();
    if (!body || !conversationId) return;
    const tempId = `tmp_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    setDraft('');
    setPending((prev) => [...prev, { tempId, body, status: 'sending' }]);
    try {
      await postBody(body);
      // Confirmed persisted — drop the optimistic bubble; server copy is now in `messages`.
      setPending((prev) => prev.filter((p) => p.tempId !== tempId));
    } catch (_) {
      // Honest failure — keep the bubble, mark failed, allow retry. Never silently drop.
      setPending((prev) => prev.map((p) => (p.tempId === tempId ? { ...p, status: 'failed' } : p)));
    }
  };

  const retry = async (tempId) => {
    const item = pending.find((p) => p.tempId === tempId);
    if (!item) return;
    setPending((prev) => prev.map((p) => (p.tempId === tempId ? { ...p, status: 'sending' } : p)));
    try {
      await postBody(item.body);
      setPending((prev) => prev.filter((p) => p.tempId !== tempId));
    } catch (_) {
      setPending((prev) => prev.map((p) => (p.tempId === tempId ? { ...p, status: 'failed' } : p)));
    }
  };

  if (!open || !peer) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Avatar className="h-8 w-8"><AvatarFallback><User className="h-4 w-4" /></AvatarFallback></Avatar>
            <div className="leading-tight">
              <div>{peer.name}</div>
              {peer.role && <div className="text-xs font-normal text-gray-500">{peer.role}</div>}
            </div>
          </DialogTitle>
          <DialogDescription>
            {t('tc.chatModal.privateDesc', { name: peer.name })}
          </DialogDescription>
        </DialogHeader>

        <div className="h-72 overflow-y-auto border rounded-md p-3 bg-gray-50 space-y-2">
          {loading && <p className="text-center text-sm text-gray-500">{t('tc.chatModal.opening')}</p>}
          {error && <p className="text-center text-sm text-red-600">{error}</p>}
          {!loading && !error && messages.length === 0 && pending.length === 0 && (
            <p className="text-center text-sm text-gray-500">{t('tc.chatModal.noMessages')}</p>
          )}
          {messages.map((m) => {
            const mine = m.sender_uid === selfUid;
            return (
              <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${mine ? 'bg-blue-600 text-white' : 'bg-white border'}`}>
                  {m.body}
                </div>
              </div>
            );
          })}
          {/* Optimistic outbound bubbles — honest sending/failed indicators (always "mine"). */}
          {pending.map((p) => (
            <div key={p.tempId} className="flex justify-end">
              <div className="max-w-[75%]">
                <div className={`rounded-lg px-3 py-2 text-sm ${p.status === 'failed' ? 'bg-red-100 text-red-800 border border-red-300' : 'bg-blue-400 text-white'}`}>
                  {p.body}
                </div>
                {p.status === 'sending' && (
                  <div className="flex items-center justify-end gap-1 text-[11px] text-gray-500 mt-0.5">
                    <Clock className="h-3 w-3" /> {t('tc.chatModal.sending')}
                  </div>
                )}
                {p.status === 'failed' && (
                  <div className="flex items-center justify-end gap-1 text-[11px] text-red-600 mt-0.5">
                    <AlertCircle className="h-3 w-3" /> {t('tc.chatModal.failed')}{' '}
                    <button type="button" className="underline" onClick={() => retry(p.tempId)}>{t('common.retry')}</button>
                  </div>
                )}
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); send(); } }}
            placeholder={t('tc.chatModal.inputPlaceholder')}
            disabled={!conversationId}
          />
          <Button onClick={send} disabled={!conversationId || !draft.trim()}>
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StaffChatModal;
